import React, {
    useEffect,
    useState,
    useContext,
    useMemo,
    useRef,
    useCallback,
    memo,
    useTransition,
} from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    MHeader,
    MButton,
    LoadingContext,
    M_StateActions,
    NavigateContext,
} from "src/M_Components";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";
import DynamicForm from "src/components/Forms/DynamicForm";
import EsriGenericMap from "src/components/Maps/EsriGenericMap";
import "../pages.css";

/* --------------------------------------------------------------------------------------------------------------------
 * nextPaint / afterPaint
 *
 * Small synchronization primitives for "visual completion".
 *
 * Purpose:
 * - Some ArcGIS operations resolve before the browser paints the final WebGL frame.
 * - Waiting for one or two animation frames allows the highlight and extent to become visible reliably.
 *
 * Notes:
 * - This is aligned with the browser render loop (not duration-based).
 * - Frame count is configurable to balance smoothness and responsiveness.
 * ------------------------------------------------------------------------------------------------------------------ */
const nextPaint = () => new Promise((resolve) => requestAnimationFrame(resolve));

const afterPaint = async (frames = 1) => {
    const count = Number.isFinite(frames) ? Math.max(1, Math.trunc(frames)) : 1;
    for (let i = 0; i < count; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await nextPaint();
    }
};

/* --------------------------------------------------------------------------------------------------------------------
 * isAbortError
 *
 * Detects AbortError-like failures across different runtimes.
 *
 * Purpose:
 * - ArcGIS interactions are frequently cancellable (select, hitTest, gestures, reconfigurations).
 * - Cancellation is expected control-flow and should not be treated as a runtime error.
 * ------------------------------------------------------------------------------------------------------------------ */
const isAbortError = (e) => {
    if (!e) return false;
    if (e?.name === "AbortError") return true;
    if (e?.message === "Aborted") return true;
    const msg = String(e?.message || "");
    return msg.toLowerCase().includes("aborted");
};

/* --------------------------------------------------------------------------------------------------------------------
 * getBoundMapAction
 *
 * Returns a "this-bound" mapAction function.
 *
 * Purpose:
 * - Some imperative map APIs rely on `this` for internal state.
 * - Binding keeps the function context stable when the method is passed around.
 * - Both direct instances and `.current` refs are supported.
 * ------------------------------------------------------------------------------------------------------------------ */
const getBoundMapAction = (componentOrRef) => {
    const c = componentOrRef?.current ? componentOrRef.current : componentOrRef;
    if (c && typeof c.mapAction === "function") return c.mapAction.bind(c);
    return null;
};

/**
 * CRUD screen for the Continent entity.
 *
 * Responsibilities:
 * - Load DynamicForm schema options and entity details depending on the operation mode
 * - Persist and restore per-tab state snapshots
 * - Render a DynamicForm and an ArcGIS map for selecting/highlighting continents
 * - Synchronize map selections with form values
 * - Update the tab label according to operation and current entity name
 *
 * Map behavior:
 * - Uses selectByAttribute on the "World_Continents" layer
 * - Prefers the layer OID field (FID) to make selection deterministic
 * - Uses highlight + optional zoom-to-extent for view mode
 * - Avoids redundant selection calls using a lightweight de-duplication key
 *
 * Rendering behavior:
 * - Uses a ResizeObserver to ensure the WebGL canvas is resized when the container size changes
 * - Uses a responsive height (clamp) to keep the map visible on small screens
 */
export default function ContinentConfigCrud({ handleTabClose, toastMessages }) {
    /**
     * Redux dispatch for shared state updates and per-tab snapshot persistence.
     */
    const dispatch = useDispatch();

    /**
     * Shared application state used for tab/session snapshots and transient globals.
     */
    const M_State = useSelector((state) => state.StateKeyValue);

    /**
     * Translation helpers and current language code.
     */
    const { t, i18n } = useTranslation();

    /**
     * Loading context used to show/hide a loading indicator and to access the base API URL.
     */
    const ctx = useContext(LoadingContext);

    /**
     * Navigation context used to rename the active tab.
     */
    const { renameTab } = useContext(NavigateContext);

    /**
     * Selected tab id retrieved from shared state.
     * Used as the per-tab snapshot key.
     */
    const selectedTab = useSelector((s) =>
        s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
    );

    /**
     * React transition signal used to reflect pending UI commits in loading logic.
     */
    const [isPendingUi, startUiTransition] = useTransition();

    /**
     * A small tick used to re-evaluate derived values when only refs change.
     */
    const [selectionTick, setSelectionTick] = useState(0);

    /**
     * Forces derived memo/effects to recompute when ref-only state changes.
     */
    const bumpSelectionTick = useCallback(() => {
        setSelectionTick((x) => x + 1);
    }, []);

    /**
     * Global operation and line may be injected by upstream navigation logic.
     * These are consumed once and then removed from shared state.
     */
    const globalOperation = M_State.find((obj) => obj.key === "operation")?.value;
    const globalLine = M_State.find((obj) => obj.key === "ContinentLine")?.value;

    /**
     * Per-tab session identifier (typically equals selectedTab).
     */
    const [id, setId] = useState(
        M_State.find((obj) => obj.key === "selectedTab")?.value
    );

    /**
     * Current operation mode: create/edit/update/view.
     */
    const [operation, setOperation] = useState(globalOperation ?? "Error");

    /**
     * Session line data containing entity identifiers and contextual values.
     */
    const [sessionLine, setSessionLine] = useState(globalLine ?? {});

    /**
     * Current entity data bound to the DynamicForm.
     */
    const [continentInfo, setContinentInfo] = useState({});

    /**
     * Flag used to prevent some effects from running during initial hydration.
     */
    const [isFirst, setIsFirst] = useState(true);

    /**
     * DynamicForm schema describing fields and constraints.
     */
    const [schema, setSchema] = useState([]);

    /**
     * Imperative DynamicForm API reference.
     * Used to submit and to reset values without re-rendering the form component itself.
     */
    const formRef = useRef(null);

    /* =====================================================================================================================
     * Ref mirrors
     *
     * Purpose:
     * - Keeps async flows and callbacks aligned with latest values.
     * - Avoids stale closure usage across delayed operations.
     * =================================================================================================================== */

    const idRef = useRef(id);
    const operationRef = useRef(operation);
    const sessionLineRef = useRef(sessionLine);
    const continentInfoRef = useRef(continentInfo);

    useEffect(() => {
        idRef.current = id;
    }, [id]);

    useEffect(() => {
        operationRef.current = operation;
    }, [operation]);

    useEffect(() => {
        sessionLineRef.current = sessionLine;
    }, [sessionLine]);

    useEffect(() => {
        continentInfoRef.current = continentInfo;
    }, [continentInfo]);

    /**
     * Token used to invalidate pending async operations when the tab context changes.
     * Incremented when `id` changes.
     */
    const tabTokenRef = useRef(0);
    useEffect(() => {
        tabTokenRef.current += 1;
    }, [id]);

    /* =====================================================================================================================
     * Backend services
     * =================================================================================================================== */

    const svcContinent = useMemo(
        () => new BufferService("Continents", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );
    const svcCreate = useMemo(
        () => new BufferService("Continents/create", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );
    const svcUpdate = useMemo(
        () => new BufferService("Continents/update", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    /* =====================================================================================================================
     * Map configuration
     * =================================================================================================================== */

    /**
     * Layer definition for the Continents feature service.
     *
     * Notes:
     * - The title must match the layer name used by mapAction("selectByAttribute", { layer: "Continents", ... }).
     * - The World_Continents layer uses FID as the OID field.
     */
    const continentLayer = useMemo(
        () => [
            {
                title: "Continents",
                type: "serviceLayer",
                url: "https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/World_Continents/FeatureServer/0",
                symbol: {
                    type: "simple-fill",
                    color: [0, 0, 0, 0],
                    outline: { type: "simple-line", color: [0, 0, 0, 255], width: 1 },
                },
            },
        ],
        []
    );

    /**
     * Map widgets configuration (kept stable via useMemo).
     */
    const mapWidgets = useMemo(() => ({ search: { position: "top-right" } }), []);

    /**
     * Map initial center and feature-info layer list.
     */
    const mapCenter = useMemo(() => [-9.14, 38.72], []);
    const continentFeatureInfoLayers = useMemo(() => ["Continents"], []);

    /**
     * Initial zoom and map constraints.
     *
     * Notes:
     * - Continents selection may require zooming out below 2 to fit the extent.
     * - minZoom is set to 0 to allow zoom-to-extent to zoom out when required.
     */
    const initialZoom = 2;

    const mapConstraints = useMemo(
        () => ({
            minZoom: initialZoom,
        }),
        []
    );

    /**
     * Reference to the map component API instance (assigned on onComponentReady).
     */
    const mapComponentRef = useRef(null);

    /**
     * Reference to the map container DOM element.
     *
     * Purpose:
     * - Supports ResizeObserver based reflow to keep WebGL canvas sized correctly.
     */
    const mapHostRef = useRef(null);

    /* =====================================================================================================================
     * Operation normalization
     * =================================================================================================================== */

    const normalizedOperation = useMemo(() => {
        const raw = String(operation || "").trim();
        const low = raw.toLowerCase();
        if (low === "create") return "create";
        if (low === "edit") return "edit";
        if (low === "update") return "edit";
        if (low === "view") return "view";
        return low || "view";
    }, [operation]);

    const isViewMode = normalizedOperation === "view";

    const mapMode = useMemo(() => {
        if (normalizedOperation === "create") return "create";
        if (normalizedOperation === "edit") return "update";
        return "view";
    }, [normalizedOperation]);

    const mapLocale = useMemo(() => {
        const lng = String(i18n.language || "pt").toLowerCase();
        if (lng.startsWith("pt")) return "pt-PT";
        if (lng.startsWith("es")) return "es-ES";
        return "en-US";
    }, [i18n.language]);

    /* =====================================================================================================================
     * Continent name mapping (localized -> service attribute)
     * =================================================================================================================== */

    /**
     * normalizeText
     *
     * Removes diacritics and normalizes to lower case.
     * Used to map localized names to ArcGIS service attribute values deterministically.
     */
    const normalizeText = useCallback((s) => {
        return String(s || "")
            .trim()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();
    }, []);

    /**
     * toServiceContinentName
     *
     * Converts localized labels into the English values used by the ArcGIS layer.
     * The service uses English continent names in the CONTINENT field.
     */
    const toServiceContinentName = useCallback(
        (raw) => {
            const n = normalizeText(raw);

            const dict = {
                europa: "Europe",
                europe: "Europe",
                asia: "Asia",
                "ásia": "Asia",
                africa: "Africa",
                "áfrica": "Africa",
                "america do norte": "North America",
                "américa do norte": "North America",
                norteamerica: "North America",
                "north america": "North America",
                "america del norte": "North America",
                "américa del norte": "North America",
                "america do sul": "South America",
                "américa do sul": "South America",
                "south america": "South America",
                "america del sur": "South America",
                "américa del sur": "South America",
                oceania: "Oceania",
                "oceânia": "Oceania",
                antartida: "Antarctica",
                "antártida": "Antarctica",
                antarctica: "Antarctica",
            };

            if (dict[n]) return dict[n];

            if (n.includes("europ")) return "Europe";
            if (n.includes("asi")) return "Asia";
            if (n.includes("afric")) return "Africa";
            if (n.includes("ocea")) return "Oceania";
            if (n.includes("antar")) return "Antarctica";
            if (n.includes("norte") && n.includes("america")) return "North America";
            if ((n.includes("sur") || n.includes("sul")) && n.includes("america"))
                return "South America";
            if (n.includes("north") && n.includes("america")) return "North America";
            if (n.includes("south") && n.includes("america")) return "South America";

            return String(raw || "").trim();
        },
        [normalizeText]
    );

    // ============================================================================================================
    // =========================================== MAP: SELECT / LOADING ==========================================
    // ============================================================================================================

    /**
     * lastSelectionCleanupRef
     *
     * Stores cleanup callbacks returned from mapAction("selectByAttribute").
     * These callbacks remove highlight and filter state from the map.
     */
    const lastSelectionCleanupRef = useRef({
        removeFilter: null,
        removeHighlight: null,
    });

    /**
     * selectionTokenRef
     *
     * Token used to ignore out-of-order selection results.
     * Each new selection increments the token. Late results from older selections are discarded.
     */
    const selectionTokenRef = useRef(0);

    /**
     * lastSelectionKeyRef
     *
     * De-duplicates selection requests that target the same feature.
     * This reduces redundant highlight work and keeps the map more responsive.
     */
    const lastSelectionKeyRef = useRef(null);

    /**
     * clearLastSelection
     *
     * Clears the last stored selection state by invoking any stored cleanup callbacks.
     */
    const clearLastSelection = useCallback(() => {
        try {
            lastSelectionCleanupRef.current.removeHighlight?.();
        } catch {
            /* noop */
        }
        try {
            lastSelectionCleanupRef.current.removeFilter?.();
        } catch {
            /* noop */
        }
        lastSelectionCleanupRef.current = { removeFilter: null, removeHighlight: null };
    }, []);

    /**
     * Selection loading scope
     *
     * The loader is derived from:
     * - selection workflow being active
     * - in-flight async work
     * - selection workflow marked complete
     * - highlight painted (afterPaint)
     * - React transition still pending
     */
    const selectLoadingActiveRef = useRef(false);
    const selectInflightRef = useRef(0);
    const selectCompletedRef = useRef(false);
    const selectHighlightPaintedRef = useRef(false);

    const beginSelectionLoading = useCallback(() => {
        selectLoadingActiveRef.current = true;
        selectInflightRef.current = 0;
        selectCompletedRef.current = false;
        selectHighlightPaintedRef.current = false;
        bumpSelectionTick();
        ctx.setIsLoad(true);
    }, [bumpSelectionTick, ctx]);

    const beginSelectionAsync = useCallback(() => {
        selectInflightRef.current += 1;
        bumpSelectionTick();
    }, [bumpSelectionTick]);

    const endSelectionAsync = useCallback(() => {
        selectInflightRef.current = Math.max(0, selectInflightRef.current - 1);
        bumpSelectionTick();
    }, [bumpSelectionTick]);

    const markSelectionCompleted = useCallback(() => {
        selectCompletedRef.current = true;
        bumpSelectionTick();
    }, [bumpSelectionTick]);

    const markSelectionHighlightPainted = useCallback(() => {
        selectHighlightPaintedRef.current = true;
        bumpSelectionTick();
    }, [bumpSelectionTick]);

    /**
     * derivedSelectionLoading
     *
     * Derived loader value computed from ref-only state plus React transition signal.
     * The selectionTick dependency forces recomputation when refs are updated.
     */
    const derivedSelectionLoading = useMemo(() => {
        if (!selectLoadingActiveRef.current) return false;

        const inflight = selectInflightRef.current > 0;
        const completed = selectCompletedRef.current;
        const painted = selectHighlightPaintedRef.current;

        return inflight || !completed || !painted || isPendingUi;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPendingUi, selectionTick]);

    /**
     * Synchronizes derived loading state to the shared loading context.
     * When selection loading finishes, scope flags are reset.
     */
    useEffect(() => {
        if (!selectLoadingActiveRef.current) return;

        ctx.setIsLoad(!!derivedSelectionLoading);

        if (!derivedSelectionLoading) {
            selectLoadingActiveRef.current = false;
            selectInflightRef.current = 0;
            selectCompletedRef.current = false;
            selectHighlightPaintedRef.current = false;
        }
    }, [ctx, derivedSelectionLoading]);

    /**
     * buildWhereFromPick
     *
     * Builds the where clause for ArcGIS selectByAttribute.
     *
     * Rules:
     * - Prefer FID when available (OID match is deterministic and efficient).
     * - Otherwise match CONTINENT exactly using the service's English name.
     */
    const buildWhereFromPick = useCallback((rawName, rawFid) => {
        if (rawFid !== null && rawFid !== undefined && rawFid !== "") {
            const fidNum = Number(rawFid);
            if (Number.isFinite(fidNum)) return `FID = ${Math.trunc(fidNum)}`;
        }

        const name = String(rawName || "").trim();
        if (!name) return "1=0";

        const safe = name.replace(/'/g, "''");
        return `CONTINENT = '${safe}'`;
    }, []);

    /**
     * selectContinentOnMap
     *
     * Applies a map highlight/filter based on the selected continent and optionally zooms to its extent.
     *
     * Behavior:
     * - Uses mapAction("selectByAttribute") with highlightGeometry and optional zoomToExtent.
     * - Stores cleanup callbacks for subsequent clearing.
     * - Discards late results using selectionTokenRef.
     * - De-duplicates identical selections using lastSelectionKeyRef.
     */
    const selectContinentOnMap = useCallback(
        async (continentName, fid, { zoomToExtent } = { zoomToExtent: false }) => {
            const c = mapComponentRef.current;

            const name = String(continentName || "").trim();
            if (!name && fid == null) return null;

            const action = getBoundMapAction(c);
            if (!action) return null;

            const where = buildWhereFromPick(name, fid);
            if (!where || where === "1=0") return null;

            const selectionKey = `layer:Continents|where:${where}|zoom:${zoomToExtent ? "1" : "0"}`;
            if (lastSelectionKeyRef.current === selectionKey) return null;
            lastSelectionKeyRef.current = selectionKey;

            const myToken = ++selectionTokenRef.current;

            try {
                const res = await action("selectByAttribute", {
                    layer: "Continents",
                    where,
                    filterGeometry: false,
                    zoomToExtent: !!zoomToExtent,
                    highlightGeometry: true,
                    extentFactor: !!zoomToExtent ? 1.02 : 1.0,
                });

                const removeFilter =
                    typeof res?.removeFilter === "function" ? res.removeFilter : null;
                const removeHighlight =
                    typeof res?.removeHighlight === "function" ? res.removeHighlight : null;

                if (myToken !== selectionTokenRef.current) {
                    try {
                        removeHighlight?.();
                    } catch {
                        /* noop */
                    }
                    try {
                        removeFilter?.();
                    } catch {
                        /* noop */
                    }
                    return null;
                }

                const count = Array.isArray(res?.features) ? res.features.length : 0;

                if (count > 0) {
                    clearLastSelection();
                    lastSelectionCleanupRef.current = { removeFilter, removeHighlight };
                } else {
                    try {
                        removeHighlight?.();
                    } catch {
                        /* noop */
                    }
                    try {
                        removeFilter?.();
                    } catch {
                        /* noop */
                    }
                }

                await afterPaint(1);
                return res;
            } catch (err) {
                if (isAbortError(err)) return null;
                // eslint-disable-next-line no-console
                console.error("[ContinentConfigCrud] Map select/highlight failed:", err);
                return null;
            }
        },
        [buildWhereFromPick, clearLastSelection]
    );

    // ============================================================================================================
    // =========================================== RESPONSIVE RESIZE =============================================
    // ============================================================================================================

    /**
     * Map resize handling
     *
     * Purpose:
     * - ArcGIS WebGL canvases may not redraw correctly when their container size changes.
     * - ResizeObserver detects layout changes (stacking on mobile, tab changes, side panels).
     * - A requestAnimationFrame wrapper avoids calling into the map multiple times per layout pass.
     */
    useEffect(() => {
        const host = mapHostRef.current;
        if (!host) return undefined;

        let rafId = 0;

        const scheduleResize = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                const action = getBoundMapAction(mapComponentRef.current);
                if (!action) return;

                try {
                    action("resize");
                } catch {
                    /* noop */
                }
            });
        };

        scheduleResize();

        const ro = new ResizeObserver(() => {
            scheduleResize();
        });

        ro.observe(host);

        return () => {
            try {
                ro.disconnect();
            } catch {
                /* noop */
            }
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, []);

    // ============================================================================================================
    // =========================================== TAB LABEL / SNAPSHOTS =========================================
    // ============================================================================================================

    /**
     * Updates the tab label whenever the operation or entity name changes.
     */
    useEffect(() => {
        if (!selectedTab || !operation) return;

        const key =
            normalizedOperation === "create"
                ? "LabelNewContinent"
                : normalizedOperation === "edit"
                    ? "LabelEditContinent"
                    : "LabelViewContinent";

        const name = sessionLine?.Name ?? continentInfo?.Name ?? "";
        const base = t(key);
        const label =
            normalizedOperation === "create" || !name ? base : `${base} "${name}"`;

        renameTab(String(selectedTab), label);
    }, [
        selectedTab,
        operation,
        normalizedOperation,
        sessionLine?.Name,
        continentInfo?.Name,
        renameTab,
        t,
    ]);

    /**
     * Initial mount:
     * - Restores snapshot (if present)
     * - Consumes global operation/line
     * - Bootstraps data loading
     */
    useEffect(() => {
        const sessionForThisTab = M_State.find((o) => o.key === id)?.value || [];
        const pick = (k) => sessionForThisTab.find((x) => x.key === k)?.value;

        const snapInfo = pick("continentInfo");
        const snapIF = pick("isFirst");
        const snapOP = pick("operation");
        const snapSL = pick("sessionLine");
        const snapSchema = pick("df.schema");

        if (snapInfo) setContinentInfo(snapInfo);
        if (snapSchema) setSchema(snapSchema);

        if (snapIF !== undefined) setIsFirst(snapIF);
        else if (isFirst) setIsFirst(false);

        const seedOperation = globalOperation !== undefined ? globalOperation : snapOP;
        const seedLine = globalLine !== undefined ? globalLine : snapSL;

        if (seedOperation !== undefined) setOperation(seedOperation);
        if (seedLine) setSessionLine(seedLine);

        if (globalOperation !== undefined)
            dispatch(M_StateActions.removeM_State({ key: "operation" }));
        if (globalLine !== undefined)
            dispatch(M_StateActions.removeM_State({ key: "ContinentLine" }));

        bootstrap(seedOperation ?? operation, seedLine ?? sessionLine);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Persists a per-tab snapshot with a small debounce to reduce Redux write frequency.
     */
    const saveSnapshotTimer = useRef(null);
    useEffect(() => {
        if (!id) return;

        const sessionData = [
            { key: "continentInfo", value: continentInfo },
            { key: "isFirst", value: isFirst },
            { key: "operation", value: operation },
            { key: "sessionLine", value: sessionLine },
            { key: "df.schema", value: schema },
        ];

        if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
        saveSnapshotTimer.current = setTimeout(() => {
            dispatch(M_StateActions.addM_State({ key: id, value: sessionData }));
        }, 80);

        return () => {
            if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
        };
    }, [id, continentInfo, isFirst, operation, sessionLine, schema, dispatch]);

    /**
     * Detects selected tab changes and restores state for the new tab id.
     */
    useEffect(() => {
        const newId = M_State.find((obj) => obj.key === "selectedTab")?.value;
        if (newId !== id) {
            setId(newId);
            getSessionStorageData(newId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [M_State]);

    /**
     * Recomputes the tab label when the language changes.
     */
    useEffect(() => {
        if (!operation || isFirst) return;

        const key =
            normalizedOperation === "create"
                ? "LabelNewContinent"
                : normalizedOperation === "edit"
                    ? "LabelEditContinent"
                    : "LabelViewContinent";

        const name = sessionLine?.Name ?? continentInfo?.Name ?? "";
        const base = t(key);
        const label =
            normalizedOperation === "create" || !name ? base : `${base} "${name}"`;

        renameTab(String(selectedTab), label);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    // ============================================================================================================
    // =========================================== DATA LOADING ===================================================
    // ============================================================================================================

    /**
     * getSessionStorageData
     *
     * Restores a snapshot for a given tab id from shared state, or triggers bootstrap.
     */
    const getSessionStorageData = (sessionID) => {
        ctx.setIsLoad(true);

        const M_StateData = M_State.find((obj) => obj.key === sessionID)?.value;

        if (M_StateData) {
            const tempInfo = M_StateData.find((obj) => obj.key === "continentInfo")?.value;
            const tempIF = M_StateData.find((obj) => obj.key === "isFirst")?.value;
            const tempOP = M_StateData.find((obj) => obj.key === "operation")?.value;
            const tempSL = M_StateData.find((obj) => obj.key === "sessionLine")?.value;
            const tempSchema = M_StateData.find((obj) => obj.key === "df.schema")?.value;

            if (tempInfo || tempOP || tempSL || tempSchema) {
                setContinentInfo(tempInfo || {});
                setIsFirst(tempIF ?? false);
                setOperation(tempOP);
                setSessionLine(tempSL);
                if (tempSchema) setSchema(tempSchema);
                ctx.setIsLoad(false);
                return;
            }
        }

        bootstrap(operationRef.current, sessionLineRef.current);
    };

    /**
     * bootstrap
     *
     * Loads schema options and (when applicable) entity details.
     * Uses a tab token to ignore late completions when user switches tabs.
     */
    const bootstrap = async (op, line) => {
        const token = tabTokenRef.current;

        ctx.setIsLoad(true);
        try {
            await fetchOptions(token);

            const opLow = String(op || "").toLowerCase();
            const idToGet = line?.Id || sessionLineRef.current?.Id;

            if (opLow !== "create" && idToGet) {
                await fetchDetails(idToGet, token);
            }
        } finally {
            ctx.setIsLoad(false);
        }
    };

    /**
     * fetchOptions
     *
     * Fetches the DynamicForm schema options from the backend.
     * Guards against late completions using the provided token.
     */
    const fetchOptions = async (tokenFromCaller) => {
        const myToken = tokenFromCaller ?? tabTokenRef.current;

        try {
            const res = await svcContinent.options();

            if (isFailurePayload(res)) {
                toastMessages("error", "", apiMessage(res, t("ErrorLoadingData")));
                return;
            }

            if (myToken !== tabTokenRef.current) return;

            setSchema(Array.isArray(res) ? res : []);
        } catch (err) {
            toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
        }
    };

    /**
     * fetchDetails
     *
     * Fetches entity details by id.
     * Guards against late completions using the provided token.
     */
    const fetchDetails = async (idToGet, tokenFromCaller) => {
        const myToken = tokenFromCaller ?? tabTokenRef.current;

        try {
            const response = await svcContinent.getId(idToGet);

            if (isFailurePayload(response)) {
                toastMessages("error", "", apiMessage(response, t("ErrorLoadingData")));
                return;
            }

            if (myToken !== tabTokenRef.current) return;

            setContinentInfo(response || {});
        } catch (err) {
            toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
        }
    };

    /**
     * getRefresh
     *
     * Refreshes schema and (if applicable) details for the current tab context.
     */
    const getRefresh = (line) => {
        const effectiveLine = line || sessionLineRef.current;
        const opLow = String(operationRef.current || "").toLowerCase();

        if (opLow !== "create" && !effectiveLine?.Id) {
            ctx.setIsLoad(false);
            return;
        }

        ctx.setIsLoad(true);

        Promise.all([
            fetchOptions(),
            opLow !== "create" ? fetchDetails(effectiveLine?.Id) : Promise.resolve(),
        ]).finally(() => ctx.setIsLoad(false));
    };

    // ============================================================================================================
    // =========================================== FORM HANDLERS ==================================================
    // ============================================================================================================

    /**
     * handleFormValuesChange
     *
     * Merges DynamicForm field changes into component local state.
     * This keeps map selection and tab labels in sync with the form.
     */
    const handleFormValuesChange = useCallback((patch) => {
        if (!patch) return;
        setContinentInfo((prev) => ({ ...prev, ...patch }));
    }, []);

    /**
     * buildPayloadFromValues
     *
     * Builds the API payload from form values based on operation mode.
     */
    const buildPayloadFromValues = (values) => {
        const name = values?.Name ?? "";
        const inactive = values?.Inactive ?? false;

        const opLow = String(operationRef.current || "").toLowerCase();
        if (opLow === "create") return { name };

        return { name, inactive: !!inactive };
    };

    /**
     * onSubmitDynamic
     *
     * Submits the current form values to the backend API (create or update).
     */
    const onSubmitDynamic = useCallback(
        async (values) => {
            ctx.setIsLoad(true);
            try {
                const payload = buildPayloadFromValues(values);

                if (!payload.name) {
                    toastMessages("error", "", t("MandatoryFields"));
                    return;
                }

                const isCreate =
                    String(operationRef.current || "").toLowerCase() === "create";

                const res = isCreate
                    ? await svcCreate.post(payload)
                    : await svcUpdate.update(sessionLineRef.current?.Id, payload);

                if (isFailurePayload(res)) {
                    toastMessages("error", "", apiMessage(res, t("ErrorSaving")));
                    return;
                }

                toastMessages(
                    "success",
                    "",
                    isCreate ? t("ContinentCreated") : t("ContinentUpdated")
                );
                handleTabClose(idRef.current);
            } catch (err) {
                toastMessages("error", "", apiMessage(err, t("ErrorSaving")));
            } finally {
                ctx.setIsLoad(false);
            }
        },
        [svcCreate, svcUpdate, ctx, t, toastMessages, handleTabClose]
    );

    /**
     * triggerFormSubmit
     *
     * Triggers DynamicForm submission through the imperative formRef API.
     */
    const triggerFormSubmit = () => {
        formRef.current?.submit?.();
    };

    /**
     * applySelectionToForm
     *
     * Applies continent selection into local state and the DynamicForm.
     *
     * Notes:
     * - EsriFid is stored as an optional runtime-only field to support deterministic view-mode selection.
     */
    const applySelectionToForm = useCallback(
        (nextName, fid) => {
            if (!nextName && fid == null) return;

            const patch = {};
            if (nextName) patch.Name = nextName;

            if (fid != null && fid !== "" && Number.isFinite(Number(fid))) {
                patch.EsriFid = Math.trunc(Number(fid));
            }

            startUiTransition(() => {
                setContinentInfo((prev) => ({ ...prev, ...patch }));
            });

            const api = formRef.current;
            if (api?.getValues && api?.reset) {
                const current = api.getValues() || {};
                startUiTransition(() => {
                    api.reset(
                        { ...current, ...patch },
                        { keepDirty: true, keepTouched: true, keepErrors: true }
                    );
                });
            }
        },
        [startUiTransition]
    );

    // ============================================================================================================
    // =========================================== MAP EVENTS =====================================================
    // ============================================================================================================

    /**
     * lastPickedRef
     *
     * Suppresses duplicate picks emitted in quick succession.
     * This can happen when the map returns near-duplicate hit results.
     */
    const lastPickedRef = useRef({ key: null, ts: 0 });

    /**
     * handleMapFeatureInfo
     *
     * Triggered when a user clicks a continent feature on the map (create/edit modes).
     *
     * Flow:
     * - Extract continent attributes (name + FID)
     * - De-duplicate near-identical events
     * - Patch form values (Name + EsriFid)
     * - Highlight selection on map without zoom
     */
    const handleMapFeatureInfo = useCallback(
        async (res) => {
            const first = Array.isArray(res) ? res[0] : res;
            if (isViewMode) return;
            if (!first) return;

            const attrs = first?.attributes || first?.feature?.attributes || {};

            const name =
                attrs.CONTINENT ??
                attrs.Continent ??
                attrs.continent ??
                attrs.NAME ??
                attrs.Name ??
                attrs.displayFieldName;

            const fid =
                attrs.FID ??
                attrs.fid ??
                attrs.ObjectId ??
                attrs.OBJECTID ??
                attrs.objectid;

            const key =
                fid != null ? `fid:${String(fid)}` : name ? `name:${String(name)}` : null;

            if (key) {
                const now = Date.now();
                if (
                    lastPickedRef.current.key === key &&
                    now - lastPickedRef.current.ts < 250
                )
                    return;
                lastPickedRef.current = { key, ts: now };
            }

            if (!name && fid == null) return;

            const rawName = String(name || "").trim();
            const serviceName = toServiceContinentName(rawName);

            beginSelectionLoading();

            applySelectionToForm(rawName, fid);
            markSelectionCompleted();

            await selectContinentOnMap(serviceName, fid, { zoomToExtent: false });
            markSelectionHighlightPainted();
        },
        [
            isViewMode,
            toServiceContinentName,
            beginSelectionLoading,
            applySelectionToForm,
            markSelectionCompleted,
            selectContinentOnMap,
            markSelectionHighlightPainted,
        ]
    );

    /**
     * highlightTimerRef
     *
     * Retry mechanism for view-mode highlighting when the layer is not ready yet.
     */
    const highlightTimerRef = useRef(null);

    /**
     * lastViewHighlightKeyRef
     *
     * Prevents repeated highlight execution when the view-mode target has not changed.
     */
    const lastViewHighlightKeyRef = useRef(null);

    /**
     * highlightContinentInView
     *
     * Highlights and zooms to the current continent in view mode.
     *
     * Behavior:
     * - Attempts selectByAttribute with zoom-to-extent
     * - Retries once if the selection returns no features
     * - Falls back to highlight-only when zoom-to-extent does not yield results
     */
    const highlightContinentInView = useCallback(() => {
        if (!isViewMode) return;

        const rawName = String(continentInfoRef.current?.Name || "").trim();
        const fidCandidate = continentInfoRef.current?.EsriFid;

        if (!rawName && fidCandidate == null) return;

        const serviceName = toServiceContinentName(rawName);

        const key = `view:${serviceName}|fid:${fidCandidate ?? ""}`;
        if (lastViewHighlightKeyRef.current === key) return;
        lastViewHighlightKeyRef.current = key;

        beginSelectionLoading();
        beginSelectionAsync();

        if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);

        const finalizeOk = async () => {
            endSelectionAsync();
            markSelectionCompleted();
            await afterPaint(1);
            markSelectionHighlightPainted();
        };

        const doHighlightOnly = async () => {
            try {
                await selectContinentOnMap(serviceName, fidCandidate, { zoomToExtent: false });
            } finally {
                await finalizeOk();
            }
        };

        const doZoomAndHighlight = async () => {
            try {
                await selectContinentOnMap(serviceName, fidCandidate, { zoomToExtent: true });
            } finally {
                await finalizeOk();
            }
        };

        const action = getBoundMapAction(mapComponentRef.current);
        if (!action) {
            highlightTimerRef.current = setTimeout(() => {
                doZoomAndHighlight();
            }, 400);
            return;
        }

        selectContinentOnMap(serviceName, fidCandidate, { zoomToExtent: true })
            .then((res) => {
                const count = Array.isArray(res?.features) ? res.features.length : 0;
                if (count > 0) {
                    finalizeOk();
                    return;
                }

                highlightTimerRef.current = setTimeout(() => {
                    doZoomAndHighlight().catch(() => {
                        highlightTimerRef.current = setTimeout(() => {
                            doHighlightOnly();
                        }, 250);
                    });
                }, 400);
            })
            .catch((e) => {
                if (isAbortError(e)) {
                    highlightTimerRef.current = setTimeout(() => {
                        doZoomAndHighlight().catch(() => {
                            highlightTimerRef.current = setTimeout(() => {
                                doHighlightOnly();
                            }, 250);
                        });
                    }, 400);
                    return;
                }

                highlightTimerRef.current = setTimeout(() => {
                    doZoomAndHighlight().catch(() => {
                        highlightTimerRef.current = setTimeout(() => {
                            doHighlightOnly();
                        }, 250);
                    });
                }, 400);
            });
    }, [
        isViewMode,
        toServiceContinentName,
        beginSelectionLoading,
        beginSelectionAsync,
        endSelectionAsync,
        markSelectionCompleted,
        markSelectionHighlightPainted,
        selectContinentOnMap,
    ]);

    /**
     * Clears any pending view-mode retry timer on unmount.
     */
    useEffect(() => {
        return () => {
            if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
        };
    }, []);

    /**
     * Runs view-mode highlighting only when the selection target changes.
     */
    useEffect(() => {
        if (!isViewMode) return;

        const rawName = String(continentInfo?.Name || "").trim();
        const fidCandidate = continentInfo?.EsriFid;

        const serviceName = toServiceContinentName(rawName);
        const key = `view:${serviceName}|fid:${fidCandidate ?? ""}`;

        if (!rawName && fidCandidate == null) return;
        if (lastViewHighlightKeyRef.current === key) return;

        highlightContinentInView();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isViewMode, continentInfo?.Name, continentInfo?.EsriFid]);

    /**
     * handleMapReady
     *
     * Receives the map component API instance when the map becomes ready.
     * Triggers initial highlight based on operation mode.
     */
    const handleMapReady = useCallback(
        (component) => {
            mapComponentRef.current = component;

            if (isViewMode) {
                highlightContinentInView();
                return;
            }

            const rawName = String(continentInfoRef.current?.Name || "").trim();
            const fidCandidate = continentInfoRef.current?.EsriFid;

            if (rawName || fidCandidate != null) {
                const serviceName = toServiceContinentName(rawName);
                selectContinentOnMap(serviceName, fidCandidate, { zoomToExtent: false });
            }
        },
        [isViewMode, highlightContinentInView, selectContinentOnMap, toServiceContinentName]
    );

    // ============================================================================================================
    // =========================================== UI KEYS / MAP PANEL ============================================
    // ============================================================================================================

    /**
     * dynamicFormKey
     *
     * Forces DynamicForm remount when operation or entity identity changes.
     */
    const dynamicFormKey = useMemo(() => {
        const op = operation || "View";
        const entityId = sessionLine?.Id ?? "new";
        return `df-continents-${selectedTab}-${op}-${entityId}`;
    }, [selectedTab, operation, sessionLine?.Id]);

    /**
     * MapPanel
     *
     * Memoized map wrapper.
     *
     * Purpose:
     * - Insulates the WebGL subtree from frequent parent re-renders.
     * - Uses a responsive height on small screens via clamp().
     * - Avoids contain:size because WebGL canvases rely on accurate layout sizing.
     */
    const MapPanel = useMemo(() => {
        return memo(function MapPanelInner({
            mode,
            locale,
            widgets,
            layers,
            center,
            zoom,
            constraints,
            onComponentReady,
            featureInfoLayers,
            onFeatureInfo,
        }) {
            return (
                <div
                    ref={mapHostRef}
                    style={{
                        height: "clamp(500px, 60vh, 520px)",
                        border: "2px solid var(--color-red-dark)",
                        borderRadius: 8,
                        overflow: "hidden",
                        position: "relative",
                        boxSizing: "border-box",
                        contain: "layout paint",
                    }}
                >
                    <EsriGenericMap
                        mode={mode}
                        locale={locale}
                        widgets={widgets}
                        layers={layers}
                        center={center}
                        zoom={zoom}
                        constraints={constraints}
                        onComponentReady={onComponentReady}
                        featureInfoLayers={featureInfoLayers}
                        onFeatureInfo={onFeatureInfo}
                        onMapClick={undefined}
                        configDebounceMs={180}
                    />
                </div>
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ============================================================================================================
    // =========================================== RENDER =========================================================
    // ============================================================================================================

    return (
        <>
            <MHeader
                element={
                    <>
                        <MButton
                            tooltip={t("RefreshTable")}
                            icon={<i className="pi pi-refresh iconTable"></i>}
                            onClick={() => getRefresh(sessionLine)}
                            label={t("RefreshTable")}
                        />
                        {!isViewMode && (
                            <MButton
                                tooltip={t("Save")}
                                icon={<i className="pi pi-save iconTable"></i>}
                                onClick={triggerFormSubmit}
                                label={t("Save")}
                            />
                        )}
                    </>
                }
            />

            <div style={{ padding: "20px", background: "var(--color-white)" }}>
                <div className="firstDivContainer">
                    <div className="continent-config-layout">
                        <div className="continent-config-form">
                            <DynamicForm
                                key={dynamicFormKey}
                                formRef={formRef}
                                mode={normalizedOperation}
                                schema={schema}
                                data={continentInfo}
                                onValuesChange={handleFormValuesChange}
                                onSubmit={onSubmitDynamic}
                                title={t("Continent Information")}
                                containerClassName="alignInputsUserForm"
                                align="vertical"
                            />
                        </div>

                        <div className="continent-config-map">
                            <MapPanel
                                mode={mapMode}
                                locale={mapLocale}
                                widgets={mapWidgets}
                                layers={continentLayer}
                                center={mapCenter}
                                zoom={initialZoom}
                                constraints={mapConstraints}
                                onComponentReady={handleMapReady}
                                featureInfoLayers={!isViewMode ? continentFeatureInfoLayers : undefined}
                                onFeatureInfo={!isViewMode ? handleMapFeatureInfo : undefined}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/**
 * Runtime prop type validation for ContinentConfigCrud.
 */
ContinentConfigCrud.propTypes = {
    handleTabClose: PropTypes.func,
    toastMessages: PropTypes.func,
};
